import * as React from 'react';

import CsvInviteModal from 'bundles/enterprise-admin-learners/components/invite-learners/CsvInviteModal';
import DomainAllowlistModal from 'bundles/enterprise-admin-learners/components/invite-learners/DomainAllowlistModal';
import EmailInviteModal from 'bundles/enterprise-admin-learners/components/invite-learners/EmailInviteModal';
import InviteMethodModal from 'bundles/enterprise-admin-learners/components/invite-learners/InviteMethodModal';
import { getAutoSelectedGroupId } from 'bundles/enterprise-admin-learners/components/invite-learners/getAutoSelectedGroupId';
import type {
  InviteEntryPoint,
  InviteMethod,
} from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';

type Step = 'method' | InviteMethod;

export type InviteLearnersModalProps = {
  open: boolean;
  onClose: () => void;
  entryPoint: InviteEntryPoint;
  /** Pre-selects (and, with `lockGroupSelection`, fixes) this group — Group Details only. */
  defaultGroupId?: string;
  /** True on Group Details: the group is fixed, not just pre-selected. */
  lockGroupSelection?: boolean;
  /** Refetch/callback fired once invites are sent — consumed starting with the outcome screen (Task 7). */
  onInvited: () => void;
};

/**
 * Entry point for the org-level invite flow (ENTADMIN-1951), opened identically from
 * Home, All Learners, and Group Details. Owns the method → email/CSV/domain step
 * state; each step renders as its own full dialog (see `AddToGroupModal` for the same
 * pattern), so this component has no chrome of its own.
 *
 * The email/CSV/domain sub-flows are scaffolding placeholders here — they're built out
 * in later Phase A tasks. This component's job is the step machine, the entry-point
 * copy switch, and the auto-select-and-skip computation for Group admins who manage
 * exactly one group.
 */
const InviteLearnersModal = ({
  open,
  onClose,
  entryPoint,
  defaultGroupId,
  lockGroupSelection,
}: InviteLearnersModalProps) => {
  const [step, setStep] = React.useState<Step>('method');
  const manageableGroups = useManageableGroups();
  const { getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const isGroupAdmin = !getIsOrganizationDomainAdmin();

  // Reset to the method-selection step whenever the modal is (re)opened.
  React.useEffect(() => {
    if (open) setStep('method');
  }, [open]);

  // Consumed starting with GroupAssignmentStep (Task 5): the group to skip the picker
  // for, either because Group Details locked it or a Group admin manages just one group.
  const autoSelectedGroupId = getAutoSelectedGroupId({
    lockGroupSelection,
    defaultGroupId,
    isGroupAdmin,
    manageableGroups,
  });

  const onBackToMethod = () => setStep('method');

  // Each step renders as its own full dialog; keyed by `step` instead of a
  // chain of `if` returns so adding a step is a new entry, not a new branch.
  const stepScreens: Record<Step, () => JSX.Element> = {
    method: () => <InviteMethodModal open={open} onClose={onClose} entryPoint={entryPoint} onSelectMethod={setStep} />,
    email: () => (
      <EmailInviteModal
        open={open}
        onClose={onClose}
        onBack={onBackToMethod}
        autoSelectedGroupId={autoSelectedGroupId}
        onSwitchToCsv={() => setStep('csv')}
      />
    ),
    csv: () => (
      <CsvInviteModal open={open} onClose={onClose} onBack={onBackToMethod} autoSelectedGroupId={autoSelectedGroupId} />
    ),
    domain: () => <DomainAllowlistModal open={open} onClose={onClose} onBack={onBackToMethod} />,
  };

  return stepScreens[step]();
};

export default InviteLearnersModal;
